import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { PageHeader } from '../../../components';
import {
  ReplayCard,
  ResultHeaderActions,
  ResultReport,
  ResultTabs,
} from '../components/result';
import { loadDiagnosis, previousDiagnosis, useDiagnoses } from '../record';
import { visibleTabs } from '../visibleTabs';

import type { MetricTabId } from '../resultCopy';

/**
 * 진단 결과 상세 — `/result/:id/:tab` (시안 §8.2).
 *
 * **주소가 정본이다.** 탭을 누르면 주소가 바뀌고, 그 주소를 그대로 열면 같은 탭이 선다 —
 * 이력·홈 타일의 딥링크가 어느 지표를 말하는지까지 담아야 한다. 탭 상태를 화면 안에만 두면
 * 그 링크가 늘 첫 탭으로 열린다.
 *
 * 회차는 **저장된 것**을 id로 읽는다(`loadDiagnosis`). 진단 직후의 결과 화면
 * (`ResultView`)과 다른 자리다: 그쪽은 방금 잰 회차를 컨텍스트에서 받아 그 세션 안에서만
 * 산다. 여기는 이력에서 다시 여는 길이라 새로고침에도 살아남는다.
 *
 * 히스토리를 **쌓지 않는다**(`replace`): 탭 일곱을 훑고 나서 뒤로 가기를 누르면 왔던
 * 화면이 아니라 방금 지나온 탭들을 거꾸로 되짚게 된다.
 *
 * **탭 없는 주소(`/result/:id`)는 그 회차의 종합이다** — 진단 직후 화면과 같은 본문
 * (`ResultReport`: 지속·음역·지표 탭·노래·종합 레이더). 예전에는 첫 탭으로 떨어져서, 진단
 * 직후 화면을 한 번 떠나면 종합을 다시 볼 길이 없었다. 이력 카드·추세 점이 이 주소로 온다.
 * 홈 타일은 한 지표를 보러 오는 길이라 탭 주소로 곧장 온다.
 */

/** 못 읽는 회차(지워졌거나 옛 스키마)는 이력으로 돌려보낸다 — 빈 결과 화면을 세우지 않는다. */
const FALLBACK = '/history';

export const ResultDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, tab } = useParams<{ id: string; tab?: string }>();

  // localStorage를 읽는다 — 렌더마다 다시 읽지 않도록 id가 바뀔 때만 뽑는다.
  const record = useMemo(() => (id ? loadDiagnosis(id) : null), [id]);
  const previous = useMemo(
    () => (record ? previousDiagnosis(record) : null),
    [record],
  );

  // 피로도 축은 컨디션별 이력에서 나온다 — 그 탭이 설지도 이력이 정한다.
  const history = useDiagnoses();
  // 세울 탭은 회차와 이력이 정한다(`visibleTabs`) — 값이 없는 탭은 안 선다.
  const tabs = useMemo(
    () => (record ? visibleTabs(record, history) : []),
    [record, history],
  );

  if (!record) {
    return <Navigate to={FALLBACK} replace />;
  }

  if (tab === undefined) {
    return (
      <div className="flex min-h-dvh w-full flex-col bg-background pb-10">
        <PageHeader
          title={t('web.result.detail.title')}
          right={<ResultHeaderActions recordId={record.id} />}
        />
        <div className="flex flex-col gap-4 px-[18px] pt-2">
          <ResultReport record={record} previous={previous} />
          {/* 서버에 올라간 녹음이 있을 때만 서는 카드 — 탭 화면에는 두지 않는다(테이크와
              탭이 1:1 이 아니다). 진단 직후 화면(`ResultView`)에도 두지 않는다: 마감 제출이
              끝나기 전이면 마지막 테이크의 주소가 아직 서버에 없다. */}
          <ReplayCard recordId={record.id} />
        </div>
      </div>
    );
  }

  // 그릴 탭이 하나도 없는 회차는 종합으로 보낸다 — 탭 바만 남은 화면을 세우지 않는다.
  if (tabs.length === 0) {
    return <Navigate to={`/result/${record.id}`} replace />;
  }

  // 안 서는 탭을 가리키는 주소(옛 링크·손으로 고친 주소)는 첫 가시 탭으로 갈아탄다.
  // `ResultTabs`도 모르는 탭을 자기 fallback으로 떨구지만, 그렇게만 두면 주소와 화면이
  // 다른 탭을 말한다 — 여기는 주소가 정본인 자리다.
  if (!tabs.includes(tab as MetricTabId)) {
    return <Navigate to={`/result/${record.id}/${tabs[0]}`} replace />;
  }

  return (
    <div className="flex min-h-dvh w-full flex-col bg-background pb-10">
      {/* 오른쪽 슬롯은 삭제·공유 버튼 묶음이다(`ResultHeaderActions`). 공유는 플래그가 켜졌을 때만 서고,
          두 버튼 모두 저장된 레코드를 보고 스스로 서고 사라진다. */}
      <PageHeader
        title={t('web.result.detail.title')}
        right={<ResultHeaderActions recordId={record.id} />}
      />

      <ResultTabs
        record={record}
        previous={previous}
        history={history}
        only={tabs}
        tab={tab as MetricTabId}
        onTabChange={(next) =>
          navigate(`/result/${record.id}/${next}`, { replace: true })
        }
      />
    </div>
  );
};
